/**
 * Zustand store. Single global state object for cross-component coordination.
 *
 * Keep this small. Anything that can be derived from data shouldn't live here.
 *
 * Selection history (back/forward) is maintained here so the Drawer can offer
 * browser-style navigation across cross-link clicks. The history tracks only
 * non-'none' selections; closing the drawer clears the history.
 */
import { create } from 'zustand';
import type { GraphMode } from '../data/build-graph';
import { orderLineNodes } from '../data/line-path';
import { DIFFICULTY_ORDER, type ClusterBy, type Difficulty, type SizeBy } from '../types/domain';
import type {
  Gameplan,
  LineEdge,
  LineNode,
  LineNodeRef,
} from '../types/gameplan';
import {
  loadGameplans,
  newGameplan,
  newLine,
  newLineEdge,
  newLineNode,
  saveGameplans,
  withGameplanUpdate,
  withLineUpdate,
} from './gameplan-storage';

export type Selection =
  | { kind: 'none' }
  | { kind: 'position'; id: string }
  | { kind: 'move'; id: string }
  | { kind: 'counter'; id: string; moveId: string };

/** A node the feedback form is scoped to (micro feedback). Null
 *  target = general / macro feedback not tied to any node. */
export type FeedbackTarget = {
  kind: 'position' | 'move' | 'counter';
  id: string;
  name: string;
};

export type FeedbackState = {
  open: boolean;
  target: FeedbackTarget | null;
};

function selectionKey(s: Selection): string | null {
  if (s.kind === 'none') return null;
  if (s.kind === 'counter') return `counter:${s.moveId}:${s.id}`;
  return `${s.kind}:${s.id}`;
}

/**
 * Returns true when the selection points to an entity that actually
 * exists in the current dataset. Used by ViewSwitcher consumers to
 * decide whether the Detail tab should be available — a stale
 * selection (entity deleted in a prior session, etc.) hides the tab
 * instead of taking the user into a broken Drawer screen.
 *
 * Pure / O(n) — accepts the raw arrays so callers don't need to
 * build their own lookup maps. Each panel already holds the arrays
 * in scope, so an extra Map allocation here would just be wasted
 * work for the lookup that runs once per render.
 */
export function isSelectionValid(
  sel: Selection,
  positions: { id: string }[],
  moves: { id: string }[],
  counters: { id: string }[],
): boolean {
  switch (sel.kind) {
    case 'none':
      return false;
    case 'position':
      return positions.some((p) => p.id === sel.id);
    case 'move':
      return moves.some((m) => m.id === sel.id);
    case 'counter':
      return counters.some((c) => c.id === sel.id);
  }
}

/** Which live UI element the onboarding tour is highlighting. */
export type CoachHighlight =
  | 'controls'
  | 'settings'
  | 'contribute'
  | 'search'
  | null;

export type AppState = {
  // Data
  loaded: boolean;
  setLoaded: (b: boolean) => void;

  // Interaction
  hoverId: string | null;
  setHover: (id: string | null) => void;
  clearHoverSoon: () => void;
  cancelClearHover: () => void;

  // Selection + history navigation
  selection: Selection;
  setSelection: (s: Selection, opts?: { openDrawer?: boolean }) => void;
  history: Selection[];
  historyIndex: number;
  back: () => void;
  forward: () => void;

  // Transient preview of a move from drawer-row hover or edge hover.
  // Drives the glow computation when set, overriding hoverId/selection.
  previewMoveId: string | null;
  setPreviewMoveId: (id: string | null) => void;

  // Per-selection toggle for the in-scene popup card. Defaults false
  // (card visible when there's a selection that anchors to a node).
  // Clicking the already-selected node/edge flips this so the user
  // can dismiss the card without closing the drawer. Resets to false
  // whenever the selection switches to a new target.

  // View
  mode: GraphMode;
  setMode: (m: GraphMode) => void;
  /**
   * 5-state gi filter:
   *   - 'all'         show every move regardless of gi metadata
   *   - 'gi'          show moves marked gi OR both (gi-friendly view)
   *   - 'gi-only'     show ONLY moves whose gi === 'gi' (exclusive
   *                   gi-game moves, e.g. cross-collar techniques)
   *   - 'no-gi'       show moves marked no-gi OR both
   *   - 'no-gi-only'  show ONLY moves whose gi === 'no-gi'
   * The HUD pills cycle each axis through all → axis → axis-only →
   * all so users can dial in either inclusive or exclusive views
   * without leaving the pill.
   */
  giFilter: 'all' | 'gi' | 'gi-only' | 'no-gi' | 'no-gi-only';
  setGiFilter: (g: 'all' | 'gi' | 'gi-only' | 'no-gi' | 'no-gi-only') => void;
  zoneFilter: Set<string>;
  toggleZone: (z: string) => void;
  showOrphans: boolean;
  setShowOrphans: (b: boolean) => void;
  showInterceptions: boolean;
  setShowInterceptions: (b: boolean) => void;
  // Toggle for the always-on warm→cool edge direction gradient. When
  // false, edges paint as base zone colors only (still get the active
  // pulse on selection). Surfaced as a HUD pill.
  showEdgeDirection: boolean;
  setShowEdgeDirection: (b: boolean) => void;
  // When false, edges are hidden whenever there's no active focal node
  // / edge (idle "global mode"). Hovering or selecting a node/edge
  // brings ITS incident edges back. Lets the user navigate a cleaner
  // map of just-nodes when they want.
  showEdges: boolean;
  setShowEdges: (b: boolean) => void;
  // Default expansion state of move sleeves (groups of moves between
  // the same pair of positions). 'collapsed' = render only the default
  // sub-arc by default, fan out on hover. 'expanded' = always render
  // all sub-arcs. A specific sleeve can also be locally expanded via
  // `hoveredSleeveIndex`.
  sleevesExpanded: 'collapsed' | 'expanded';
  setSleevesExpanded: (s: 'collapsed' | 'expanded') => void;
  // Which sleeve is currently hovered (so just that one fans out even
  // when the default is 'collapsed'). Indexes into graph.edges.
  hoveredSleeveIndex: number | null;
  setHoveredSleeveIndex: (i: number | null) => void;
  // User-controllable hard pause for autorotate. The auto-pause logic
  // (hoverId !== null, drawer open) still applies on top of this.
  autorotatePaused: boolean;
  setAutorotatePaused: (b: boolean) => void;
  // Active clustering attribute. 'none' = pure topology layout (default).
  clusterBy: ClusterBy;
  setClusterBy: (c: ClusterBy) => void;
  // Per-tier difficulty visibility. Default: all 4 tiers visible.
  // An entity is rendered iff its tier is in the active set (or has no
  // difficulty assigned). Nodes whose `difficulty` falls outside the
  // selected set are hidden from the graph AND from the drawer rows;
  // moves whose either endpoint is hidden are also hidden as edges.
  difficultyFilter: Set<Difficulty>;
  toggleDifficulty: (d: Difficulty) => void;
  setDifficultyFilter: (set: Set<Difficulty>) => void;
  // Collapse-variants toggle. When true (default), only the canonical
  // member of each family renders — saddle alone instead of saddle +
  // 411 + crossashi + honeyhole. Variants come back when toggled off.
  // The collapsed view is the "teaching utility" view; the expanded
  // view is the "art piece, scale of BJJ" view.
  collapseVariants: boolean;
  setCollapseVariants: (b: boolean) => void;
  // Concept filter. Empty set = show everything. Non-empty = show only
  // entities tagged with at least one of the selected concepts.
  conceptFilter: Set<string>;
  toggleConcept: (c: string) => void;
  setConceptFilter: (s: Set<string>) => void;
  /**
   * Multiplier applied to both the pre-sim and worker cluster-centroid
   * pull strengths. 1.0 reproduces the baseline tuning that fits most
   * cluster axes; raise to tighten clusters into more separated
   * canopies, lower toward 0 to relax them back toward pure topology.
   * Exposed in the HUD under the cluster section as a live slider so
   * the user can dial intensity on the fly without a code change.
   * Default 1.2 — a gentle 20% bump from baseline that keeps the
   * other cluster axes legible while making sparse-axis clusters
   * (submission-type, certain concept tags) read more clearly.
   */
  clusterStrength: number;
  setClusterStrength: (s: number) => void;
  /**
   * Knot each family together inside its primary cluster — a second
   * level of grouping rather than a different one.
   *
   * ON by default. It shipped off so that adding it would not rearrange
   * a layout people were used to, but with the map at 174 nodes the
   * coarse axis alone leaves ~90 nodes in "bottom" with no internal
   * order. The family knots are the structure that makes that readable.
   */
  familySubClusters: boolean;
  setFamilySubClusters: (v: boolean) => void;
  /**
   * What a node's size means. Defaults to `degree`, the original curve —
   * changing what the map looks like by default is not something a bug fix
   * should do on its own.
   */
  sizeBy: SizeBy;
  setSizeBy: (v: SizeBy) => void;
  /**
   * Free look — stop the camera being tethered to a node.
   *
   * Normally CameraDirector lerps the orbit target onto the selection (or
   * a neutral pose) every single frame, which is what makes the view feel
   * locked: pan the camera away and it slides straight back. Free look
   * simply stops re-asserting that target, so the orbit pivot stays
   * wherever the user leaves it and pan/zoom behave like an ordinary 3D
   * viewport.
   *
   * Selection still works and still highlights — it just no longer moves
   * the camera, which is the whole point of the mode.
   */
  freeLook: boolean;
  setFreeLook: (v: boolean) => void;
  /**
   * Per-cluster-id hide set, driven by the cluster-key row toggles.
   * When a cluster id is in here, every node whose ONLY cluster
   * memberships are in this set is filtered out of both the canvas
   * and the list view. (Multi-membership nodes — moves with several
   * concept tags — survive as long as at least one of their tags is
   * still visible.) Cleared automatically when the cluster axis or
   * graph mode changes so a stale set from a different axis doesn't
   * leak across.
   */
  hiddenClusterIds: Set<string>;
  toggleHiddenClusterId: (id: string) => void;
  setHiddenClusterIds: (ids: Set<string>) => void;
  clearHiddenClusterIds: () => void;
  // When non-null, CameraDirector overrides its selection-based target
  // and lerps the orbit point onto the cluster's live centroid instead.
  // Cleared when the user clicks a node (selection takes over again) or
  // explicitly clears it. Used by the cluster reference key in the HUD
  // to "zoom to a cluster".
  focusedClusterId: string | null;
  setFocusedClusterId: (id: string | null) => void;
  // Per-direction neighborhood expansion of a selection / hover.
  // showMovesFrom = include outgoing edges + their target nodes.
  // showMovesTo   = include incoming edges + their source nodes.
  // Both off → only the focal node itself glows; no neighbors, no
  // edges. Lets the user isolate a node with no halo when scanning a
  // dense graph. Defaults: both on (legacy behavior).
  showMovesFrom: boolean;
  setShowMovesFrom: (b: boolean) => void;
  showMovesTo: boolean;
  setShowMovesTo: (b: boolean) => void;
  // Right-pane swap. The side panel can show ONE of three modes at a
  // time: 'detail' (per-selection Drawer, the default), 'list' (the
  // filterable sheet), or 'plan' (the gameplan editor — see phase 1
  // data layer in gameplan-storage.ts). The two booleans below encode
  // a 3-state enum:
  //   listViewOpen=false + gameplanViewOpen=false → 'detail'
  //   listViewOpen=true  + gameplanViewOpen=false → 'list'
  //   listViewOpen=*     + gameplanViewOpen=true  → 'plan'
  // The setters are mutually-exclusive — turning one on forces the
  // other off — so the UI can't end up in an undefined state.
  listViewOpen: boolean;
  setListViewOpen: (b: boolean) => void;
  gameplanViewOpen: boolean;
  setGameplanViewOpen: (b: boolean) => void;
  // History index that was active when the user entered the drawer
  // from list view (via "view details"). When non-null, `back()`
  // hopping at or below this index restores the list view instead of
  // popping further. Cleared on selection-cleared or when the user
  // arrives back at the list. Lets the back button trace the user's
  // path back to where they started: list view → drawer chain →
  // back-back-back → list view.
  listViewEntryIndex: number | null;
  /** Called by ListView's "view details" affordance. Pushes the
   *  selection like setSelection would, but also records the resulting
   *  historyIndex so back() can later restore list view. */
  enterDrawerFromList: (s: Selection) => void;
  /**
   * Mirror of listViewEntryIndex for the gameplan view. When the user
   * clicks the "view details" eyeball on a node inside a gameplan
   * line, we record (a) the resulting historyIndex AND (b) the
   * gameplan / line they came from. The back button then restores
   * the gameplan view with that plan + line re-activated so the user
   * lands exactly where they left.
   */
  gameplanViewEntryIndex: number | null;
  gameplanViewReturnTarget: {
    gameplanId: string;
    lineId: string | null;
  } | null;
  enterDrawerFromGameplan: (
    s: Selection,
    gameplanId: string,
    lineId: string | null,
  ) => void;
  // Set of row keys that ListView has expanded ("position:foo", "move:bar",
  // "counter:baz"). Lifted out of ListView's component state so it
  // survives the panel unmounting: when the user closes the side panel
  // and reopens it, their previously-expanded rows stay open. Keys are
  // mode-agnostic — switching graph modes hides rows from the other
  // mode, but the keys remain so re-entering that mode keeps the same
  // expansion state.
  listViewExpanded: Set<string>;
  toggleListViewExpansion: (key: string) => void;
  expandListViewRow: (key: string) => void;

  // List-view tag filter — a set of `type:value` facets + AND/OR mode.
  // Lives in the store so a clickable chip anywhere (Drawer, ListView row)
  // can drive it. See src/ui/list-facets.ts.
  listFilterTags: string[];
  listFilterMode: 'all' | 'any';
  setListFilterMode: (m: 'all' | 'any') => void;
  addListFilterTag: (facet: string) => void;
  removeListFilterTag: (facet: string) => void;
  clearListFilterTags: () => void;
  /** One-click "show me everything with this tag": open the list, filtered
   *  to exactly this one facet. */
  filterListByTag: (facet: string) => void;
  /** Eyeball toggle in the Search panel: when on, the 3D map shows only
   *  the entities the current search/filter returns (mirrors the
   *  gameplan filter's eye). */
  searchFilter: boolean;
  setSearchFilter: (b: boolean) => void;
  /** Ids of the current search results, published by ListView so App can
   *  narrow the canvas. Null when the eye is off. */
  searchRefSets: {
    posIds: Set<string>;
    moveIds: Set<string>;
    counterIds: Set<string>;
  } | null;
  setSearchRefSets: (
    s: {
      posIds: Set<string>;
      moveIds: Set<string>;
      counterIds: Set<string>;
    } | null,
  ) => void;

  // Drawer
  drawerOpen: boolean;
  setDrawerOpen: (b: boolean) => void;
  drawerWidth: number;
  setDrawerWidth: (w: number) => void;
  /** Bottom-sheet height in pixels. Only consulted in mobile portrait
   *  (useResponsivePanelLayout). Updated by ResizeHandle during a
   *  vertical drag; persisted across opens just like drawerWidth. */
  sheetHeight: number;
  setSheetHeight: (h: number) => void;
  // True only while the user is actively dragging the drawer's resize
  // handle. App.tsx uses this to suppress the canvas's `transition: right`
  // animation so the canvas tracks the drawer in real time instead of
  // lagging 280ms behind. Open/close transitions remain animated.
  drawerResizing: boolean;
  setDrawerResizing: (b: boolean) => void;

  // ----- Feedback modal -----
  // Global so both the macro entry point (SettingsMenu) and the micro
  // entry point (Drawer per-node "suggest edit") can open the same
  // form. `target` null = macro / general feedback; non-null = micro
  // feedback pre-scoped to that node.
  feedback: FeedbackState;
  openFeedback: (target?: FeedbackTarget | null) => void;
  closeFeedback: () => void;

  // ----- Onboarding -----
  // `tourRunning` triggers the OnboardingCoach first-orbit tour. It
  // auto-runs on the first map view (gated on `bjjmap:seen-tour`), and
  // the Settings → "replay tour" affordance flips it true directly. Not
  // persisted — the localStorage `bjjmap:seen-*` keys own first-run
  // gating; this is just the in-session trigger.
  tourRunning: boolean;
  setTourRunning: (b: boolean) => void;
  // True while the coach is on a NODE step: the in-scene projector then
  // features a hub node (selects + tracks it) so the coach bubble can
  // point straight at it. Cleared on non-node steps and tour end.
  coachNodeActive: boolean;
  setCoachNodeActive: (b: boolean) => void;
  // KEY (cluster legend) expand state — lifted out of NavOverlay so the
  // onboarding coach can open it on the colour step and close it after.
  keyPanelOpen: boolean;
  setKeyPanelOpen: (b: boolean) => void;
  // Which real UI element the onboarding tour is highlighting right now,
  // so that element can flash a pulse: 'controls' (EdgeActions — also
  // force-shows all three buttons), 'key', 'settings', or 'contribute'.
  // Null when no highlight step is active.
  coachHighlight: CoachHighlight;
  setCoachHighlight: (a: CoachHighlight) => void;

  // ----- Path tutorial (guided line playback, BETA) -----
  // Step-through playback of a saved Line on the 3D map: walk node →
  // node, focusing the camera on each (via setSelection, which the
  // CameraDirector already lerps to) and surfacing its notes + the
  // outgoing edge label. `null` when not playing.
  tutorial: { lineId: string; step: number } | null;
  startTutorial: (gameplanId: string, lineId: string) => void;
  tutorialNext: () => void;
  tutorialPrev: () => void;
  endTutorial: () => void;

  // ----- Gameplan editor -----
  //
  // Persisted to localStorage via the subscription wired up at the
  // bottom of this file. Mutations here flow through the helper
  // factories / immutable-update functions in gameplan-storage.ts so
  // the action functions stay one-liners.
  //
  // `activeGameplanId` / `activeLineId` track which plan & line the
  // editor UI is currently showing. Both nullable — the editor's
  // empty state shows the gameplan index when no plan is active.
  gameplans: Gameplan[];
  activeGameplanId: string | null;
  activeLineId: string | null;
  // When true (and the gameplan view is open), the 3D graph hides every
  // position/move not referenced by the active gameplan's lines, and the
  // active line's path is glowed in place over the rest of the plan.
  gameplanFilter: boolean;
  setGameplanFilter: (b: boolean) => void;

  // Gameplan-level CRUD. The create variants return the new id so
  // callers can immediately switch focus to the freshly-created
  // entity without doing a name-search.
  createGameplan: (name: string, description?: string) => string;
  deleteGameplan: (id: string) => void;
  renameGameplan: (id: string, name: string) => void;
  setGameplanDescription: (id: string, description: string) => void;
  setActiveGameplan: (id: string | null) => void;

  // Line-level CRUD.
  createLine: (gameplanId: string, name: string) => string;
  deleteLine: (gameplanId: string, lineId: string) => void;
  renameLine: (gameplanId: string, lineId: string, name: string) => void;
  setLineNotes: (gameplanId: string, lineId: string, notes: string) => void;
  setActiveLine: (lineId: string | null) => void;

  // Node-level CRUD. removeLineNode also drops every incident edge so
  // the line graph stays consistent.
  addLineNode: (gameplanId: string, lineId: string, ref: LineNodeRef) => string;
  removeLineNode: (gameplanId: string, lineId: string, nodeId: string) => void;
  setLineNodeNotes: (
    gameplanId: string,
    lineId: string,
    nodeId: string,
    notes: string,
  ) => void;

  // Edge-level CRUD.
  addLineEdge: (
    gameplanId: string,
    lineId: string,
    fromId: string,
    toId: string,
    label?: string,
  ) => string;
  removeLineEdge: (gameplanId: string, lineId: string, edgeId: string) => void;
  setLineEdgeLabel: (
    gameplanId: string,
    lineId: string,
    edgeId: string,
    label: string,
  ) => void;
};

export const DRAWER_MIN_WIDTH = 300;
export const DRAWER_MAX_WIDTH = 900;
export const DRAWER_DEFAULT_WIDTH = 440;

// Bottom-sheet sizing for mobile portrait. Stored in pixels so the
// resize handle can drag in pixel space; the runtime upper bound is
// clamped to ~80% of viewport height in the setter so smaller phones
// don't allow the sheet to swallow the canvas entirely.
export const SHEET_MIN_HEIGHT = 220;
export const SHEET_DEFAULT_HEIGHT = 420;

let _hoverClearTimer: ReturnType<typeof setTimeout> | null = null;

export const useAppStore = create<AppState>((set, get) => ({
  loaded: false,
  setLoaded: (b) => set({ loaded: b }),

  hoverId: null,
  setHover: (id) => {
    if (_hoverClearTimer) {
      clearTimeout(_hoverClearTimer);
      _hoverClearTimer = null;
    }
    set({ hoverId: id });
  },
  clearHoverSoon: () => {
    if (_hoverClearTimer) clearTimeout(_hoverClearTimer);
    _hoverClearTimer = setTimeout(() => {
      _hoverClearTimer = null;
      set({ hoverId: null });
    }, 600);
  },
  cancelClearHover: () => {
    if (_hoverClearTimer) {
      clearTimeout(_hoverClearTimer);
      _hoverClearTimer = null;
    }
  },

  selection: { kind: 'none' },
  history: [],
  historyIndex: -1,
  setSelection: (s, opts) => {
    // `openDrawer` defaults true (historic behaviour). The onboarding
    // coach passes false so featuring a node doesn't fling the drawer
    // open — the coach owns drawer visibility per step.
    const openDrawer = opts?.openDrawer !== false;
    // Always clear preview on a real selection change.
    if (_hoverClearTimer) {
      /* don't clobber hover timer here */
    }
    if (s.kind === 'none') {
      // Full deselect.
      set({
        selection: s,
        history: [],
        historyIndex: -1,
        drawerOpen: false,
        previewMoveId: null,
        focusedClusterId: null,
        listViewEntryIndex: null,
        gameplanViewEntryIndex: null,
        gameplanViewReturnTarget: null,
      });
      return;
    }
    // Any real selection clears cluster focus — the user has moved on to
    // a specific node.
    set({ focusedClusterId: null });
    const state = get();
    const curKey = selectionKey(state.selection);
    const newKey = selectionKey(s);
    if (curKey === newKey) {
      // Same target — don't push, but make sure drawer is open.
      set({ selection: s, drawerOpen: openDrawer, previewMoveId: null });
      return;
    }
    // Truncate any forward history (browser-style), then push.
    const truncated = state.history.slice(0, state.historyIndex + 1);
    const newHistory = [...truncated, s];
    set({
      selection: s,
      history: newHistory,
      historyIndex: newHistory.length - 1,
      drawerOpen: openDrawer,
      previewMoveId: null,
    });
  },
  back: () => {
    const state = get();
    // If the user entered the drawer from gameplan view and they're
    // back at (or past) that entry, reopen gameplan view with the
    // saved plan + line re-activated. Checked BEFORE the list-view
    // branch since only one entry index can be set at a time (the
    // setters that open list/gameplan clear the other).
    if (
      state.gameplanViewEntryIndex !== null &&
      state.historyIndex <= state.gameplanViewEntryIndex
    ) {
      const target = state.gameplanViewReturnTarget;
      set({
        gameplanViewOpen: true,
        listViewOpen: false,
        gameplanViewEntryIndex: null,
        gameplanViewReturnTarget: null,
        drawerOpen: false,
        // Re-activate the gameplan / line the user came from so the
        // canvas lands exactly where they left. If the plan or line
        // was deleted in the meantime, the editor's auto-recovery
        // effects fall back gracefully.
        activeGameplanId: target?.gameplanId ?? null,
        activeLineId: target?.lineId ?? null,
      });
      return;
    }
    // If the user entered the drawer from list view and they're back
    // at (or trying to step past) that entry, reopen list view rather
    // than popping further history.
    if (
      state.listViewEntryIndex !== null &&
      state.historyIndex <= state.listViewEntryIndex
    ) {
      set({
        listViewOpen: true,
        listViewEntryIndex: null,
        drawerOpen: false,
      });
      return;
    }
    if (state.historyIndex <= 0) return;
    const newIndex = state.historyIndex - 1;
    set({
      selection: state.history[newIndex],
      historyIndex: newIndex,
      drawerOpen: true,
    });
  },
  forward: () => {
    const state = get();
    if (state.historyIndex >= state.history.length - 1) return;
    const newIndex = state.historyIndex + 1;
    set({
      selection: state.history[newIndex],
      historyIndex: newIndex,
      drawerOpen: true,
    });
  },

  previewMoveId: null,
  setPreviewMoveId: (id) => set({ previewMoveId: id }),

  // Default true: a fresh first selection opens the drawer but keeps
  // the in-scene card hidden. Re-clicking the selected node surfaces

  mode: 'position',
  setMode: (m) =>
    set((s) => {
      // Auto-reset clusterBy if the current choice doesn't apply in the
      // new mode. 'none' is permitted for legacy but not user-selectable.
      // 'family' and 'concept' work in BOTH modes (both positions and
      // moves carry those attrs), so they pass through unchanged.
      let next: ClusterBy = s.clusterBy;
      if (next === 'none' || next === 'family' || next === 'concept') {
        // hiddenClusterIds stays meaningful across modes for these
        // mode-agnostic axes.
        return { mode: m, clusterBy: next };
      }
      // posture works in both modes (move clusters by its from-
      // position's posture), so let it pass through either way.
      // Advantage and posture both work in EITHER mode — positions carry
      // dominance, moves carry performedBy, and a move borrows its
      // from-position's posture. Without this, defaulting to advantage
      // would silently reset to positionType the first time anyone
      // toggled between position and move mode.
      if (next === 'posture' || next === 'advantage') {
        return { mode: m, clusterBy: next };
      }
      if (m === 'position' && next !== 'positionType') {
        next = 'positionType';
      } else if (
        m === 'move' &&
        next !== 'moveCategory' &&
        next !== 'submissionType' &&
        next !== 'disposition'
      ) {
        next = 'moveCategory';
      }
      // When the auto-reset flips us to a different cluster axis,
      // drop the hidden set — its ids belong to the prior axis.
      return {
        mode: m,
        clusterBy: next,
        hiddenClusterIds:
          next === s.clusterBy ? s.hiddenClusterIds : new Set<string>(),
      };
    }),
  giFilter: 'all',
  setGiFilter: (g) => set({ giFilter: g }),
  zoneFilter: new Set(),
  toggleZone: (z) =>
    set((s) => {
      const next = new Set(s.zoneFilter);
      if (next.has(z)) next.delete(z);
      else next.add(z);
      return { zoneFilter: next };
    }),
  showOrphans: true,
  setShowOrphans: (b) => set({ showOrphans: b }),
  // Default OFF: interception markers are a detail layer that clutters
  // the initial overview. Users opt in from the HUD when they want
  // counter-fork detail.
  showInterceptions: false,
  setShowInterceptions: (b) => set({ showInterceptions: b }),
  // Default OFF: comet animation is opt-in. Toggle controls only the
  // traveling-glow comet (the warm/cool gradient is shelved behind
  // ENABLE_DIRECTION_GRADIENT in Edges.tsx). Initial view is calm:
  // static flat-color edges, no motion until the user enables it.
  // Default ON — the flow cones (EdgeFlow) make direction read at a
  // glance, which is worth showing before the user finds the toggle.
  showEdgeDirection: true,
  setShowEdgeDirection: (b) => set({ showEdgeDirection: b }),
  // Default OFF. Edges stay hidden in the idle ("global") state and
  // reappear when a node/edge is hovered or selected — the initial
  // view stays calm, and incident edges surface on demand.
  showEdges: false,
  setShowEdges: (b) => set({ showEdges: b }),
  // Default 'collapsed': cleaner overview, reveals on hover.
  sleevesExpanded: 'collapsed',
  setSleevesExpanded: (s) => set({ sleevesExpanded: s }),
  hoveredSleeveIndex: null,
  setHoveredSleeveIndex: (i) => set({ hoveredSleeveIndex: i }),
  // Auto-rotate is OFF by default — the map sits still until the user
  // opts in (Settings → rotation). `autorotatePaused` true = no spin.
  autorotatePaused: true,
  setAutorotatePaused: (b) => set({ autorotatePaused: b }),
  // Defaulting to positionType per user feedback: clustering reads as
  // the primary lens, and "top vs bottom vs neutral" is the most
  // intuitive starter axis.
  // Advantage is the default lens. "Is this a good place to be" is the
  // question a newcomer actually has, and it is the one axis that reads
  // without knowing any vocabulary — the others assume you already know
  // what a guard or a family is.
  clusterBy: 'advantage',
  setClusterBy: (c) =>
    // Clear hiddenClusterIds on axis change — those ids live in the
    // previous axis's namespace (e.g. "saddle" was a family id; on
    // concept it doesn't mean anything). Stale hides would silently
    // filter the new axis in surprising ways.
    set((s) => ({
      clusterBy: c,
      hiddenClusterIds: s.clusterBy === c ? s.hiddenClusterIds : new Set(),
    })),
  difficultyFilter: new Set<Difficulty>(DIFFICULTY_ORDER),
  toggleDifficulty: (d) =>
    set((s) => {
      const next = new Set(s.difficultyFilter);
      if (next.has(d)) next.delete(d);
      else next.add(d);
      return { difficultyFilter: next };
    }),
  setDifficultyFilter: (s) => set({ difficultyFilter: s }),
  // Default ON: the unfiltered "teaching" view shows canonical positions
  // only. The art-piece scale-of-BJJ view is one toggle away.
  collapseVariants: true,
  setCollapseVariants: (b) => set({ collapseVariants: b }),
  conceptFilter: new Set<string>(),
  toggleConcept: (c) =>
    set((s) => {
      const next = new Set(s.conceptFilter);
      if (next.has(c)) next.delete(c);
      else next.add(c);
      return { conceptFilter: next };
    }),
  setConceptFilter: (s) => set({ conceptFilter: s }),
  familySubClusters: true,
  setFamilySubClusters: (v) => set({ familySubClusters: v }),
  sizeBy: 'degree',
  setSizeBy: (v) => set({ sizeBy: v }),
  freeLook: false,
  setFreeLook: (v) => set({ freeLook: v }),
  clusterStrength: 1.2,
  setClusterStrength: (s) =>
    // Clamp to a sane range — 0 disables, 2.5 is the upper-bound the
    // HUD slider exposes. Beyond ~2.5 the cluster force fights the
    // radial force so aggressively that nodes overshoot their target
    // shells and the layout looks jittery.
    set({ clusterStrength: Math.max(0, Math.min(2.5, s)) }),
  hiddenClusterIds: new Set<string>(),
  toggleHiddenClusterId: (id) =>
    set((s) => {
      const next = new Set(s.hiddenClusterIds);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return { hiddenClusterIds: next };
    }),
  setHiddenClusterIds: (ids) => set({ hiddenClusterIds: ids }),
  clearHiddenClusterIds: () => set({ hiddenClusterIds: new Set() }),
  focusedClusterId: null,
  setFocusedClusterId: (id) => set({ focusedClusterId: id }),
  showMovesFrom: true,
  setShowMovesFrom: (b) => set({ showMovesFrom: b }),
  showMovesTo: true,
  setShowMovesTo: (b) => set({ showMovesTo: b }),
  listViewOpen: false,
  setListViewOpen: (b) =>
    set((s) => ({
      listViewOpen: b,
      // Mutually exclusive with the gameplan panel — opening list
      // closes plan, and vice versa.
      gameplanViewOpen: b ? false : s.gameplanViewOpen,
      // Any pending return-trail (to either side panel) becomes
      // meaningless when the user manually toggles the panel.
      listViewEntryIndex: b ? null : s.listViewEntryIndex,
      gameplanViewEntryIndex: b ? null : s.gameplanViewEntryIndex,
      gameplanViewReturnTarget: b ? null : s.gameplanViewReturnTarget,
    })),
  listFilterTags: [],
  listFilterMode: 'any',
  setListFilterMode: (m) => set({ listFilterMode: m }),
  addListFilterTag: (facet) =>
    set((s) =>
      s.listFilterTags.includes(facet)
        ? s
        : { listFilterTags: [...s.listFilterTags, facet] },
    ),
  removeListFilterTag: (facet) =>
    set((s) => ({
      listFilterTags: s.listFilterTags.filter((f) => f !== facet),
    })),
  clearListFilterTags: () => set({ listFilterTags: [] }),
  filterListByTag: (facet) =>
    set((s) => ({
      listFilterTags: [facet],
      listViewOpen: true,
      gameplanViewOpen: false,
      gameplanViewEntryIndex: s.gameplanViewOpen ? null : s.gameplanViewEntryIndex,
    })),
  // Default OFF — searching shouldn't disturb the map. The eye is opt-in
  // for when you actually want the canvas narrowed to your results;
  // having it on by default made typing in Search silently reshape the
  // graph out from under you.
  searchFilter: false,
  setSearchFilter: (b) =>
    set((s) => ({
      searchFilter: b,
      // Dropping the eye also drops the published set so the canvas
      // reverts immediately rather than on the next ListView render.
      searchRefSets: b ? s.searchRefSets : null,
    })),
  searchRefSets: null,
  setSearchRefSets: (v) => set({ searchRefSets: v }),
  gameplanViewOpen: false,
  setGameplanViewOpen: (b) =>
    set((s) => ({
      gameplanViewOpen: b,
      listViewOpen: b ? false : s.listViewOpen,
      listViewEntryIndex: b ? null : s.listViewEntryIndex,
      gameplanViewEntryIndex: b ? null : s.gameplanViewEntryIndex,
      gameplanViewReturnTarget: b ? null : s.gameplanViewReturnTarget,
      // NOTE: selection / drawerOpen / history are intentionally
      // PRESERVED when toggling the Plan view itself.
      // The Plan INDEX (no active plan picked) leaves the canvas
      // unfiltered, so any current selection is still valid. The
      // deselect we used to need (bug #14: Detail errors when the
      // gameplan filter drops the selected entity) is now handled
      // in setActiveGameplan, which is what actually engages the
      // canvas filter. Opening / closing the Plan tab without
      // picking a plan no longer yanks the user's selection.
    })),
  listViewEntryIndex: null,
  enterDrawerFromList: (s) => {
    const state = get();
    const curKey = selectionKey(state.selection);
    const newKey = selectionKey(s);
    // Mirror setSelection's push semantics, then record the resulting
    // historyIndex as the list-view return point. If the user re-clicks
    // the same target they were already on, the push is skipped — the
    // entry index reflects whatever index that target is at.
    if (curKey === newKey && state.selection.kind !== 'none') {
      set({
        selection: s,
        drawerOpen: true,
        listViewOpen: false,
        listViewEntryIndex: state.historyIndex,
        // Mutually exclusive with the gameplan entry trail.
        gameplanViewEntryIndex: null,
        gameplanViewReturnTarget: null,
        previewMoveId: null,
      });
      return;
    }
    const truncated = state.history.slice(0, state.historyIndex + 1);
    const newHistory = [...truncated, s];
    set({
      selection: s,
      history: newHistory,
      historyIndex: newHistory.length - 1,
      drawerOpen: true,
      listViewOpen: false,
      listViewEntryIndex: newHistory.length - 1,
      gameplanViewEntryIndex: null,
      gameplanViewReturnTarget: null,
      previewMoveId: null,
      focusedClusterId: null,
    });
  },
  gameplanViewEntryIndex: null,
  gameplanViewReturnTarget: null,
  enterDrawerFromGameplan: (s, gameplanId, lineId) => {
    const state = get();
    const curKey = selectionKey(state.selection);
    const newKey = selectionKey(s);
    const returnTarget = { gameplanId, lineId };
    // Mirror enterDrawerFromList — push the selection (or reuse the
    // current slot if it's already the target) and stash the gameplan
    // + line so back() can restore the editor with the right context.
    if (curKey === newKey && state.selection.kind !== 'none') {
      set({
        selection: s,
        drawerOpen: true,
        gameplanViewOpen: false,
        listViewOpen: false,
        gameplanViewEntryIndex: state.historyIndex,
        gameplanViewReturnTarget: returnTarget,
        listViewEntryIndex: null,
        previewMoveId: null,
      });
      return;
    }
    const truncated = state.history.slice(0, state.historyIndex + 1);
    const newHistory = [...truncated, s];
    set({
      selection: s,
      history: newHistory,
      historyIndex: newHistory.length - 1,
      drawerOpen: true,
      gameplanViewOpen: false,
      listViewOpen: false,
      gameplanViewEntryIndex: newHistory.length - 1,
      gameplanViewReturnTarget: returnTarget,
      listViewEntryIndex: null,
      previewMoveId: null,
      focusedClusterId: null,
    });
  },
  listViewExpanded: new Set<string>(),
  // Single-row invariant: at most one key in the set at any time.
  // Expanding a new row replaces the previous one; expanding the
  // already-active row collapses it. We keep the Set shape (instead of
  // `string | null`) so consumers can continue using `.has(key)` reads.
  toggleListViewExpansion: (key) =>
    set((s) => {
      if (s.listViewExpanded.has(key)) return { listViewExpanded: new Set() };
      return { listViewExpanded: new Set([key]) };
    }),
  expandListViewRow: (key) =>
    set((s) => {
      // No-op if this row is already the sole expanded row.
      if (s.listViewExpanded.size === 1 && s.listViewExpanded.has(key)) return {};
      return { listViewExpanded: new Set([key]) };
    }),

  drawerOpen: false,
  setDrawerOpen: (b) => set({ drawerOpen: b }),
  drawerWidth: DRAWER_DEFAULT_WIDTH,
  setDrawerWidth: (w) =>
    set({
      drawerWidth: Math.max(DRAWER_MIN_WIDTH, Math.min(DRAWER_MAX_WIDTH, w)),
    }),
  sheetHeight: SHEET_DEFAULT_HEIGHT,
  setSheetHeight: (h) =>
    set(() => {
      // Upper bound is computed at write-time against the live
      // viewport so smaller devices can't drag the sheet past 85% of
      // the screen height (leaves room for the bottom system bar).
      const maxH =
        typeof window === 'undefined'
          ? h
          : Math.floor(window.innerHeight * 0.85);
      return { sheetHeight: Math.max(SHEET_MIN_HEIGHT, Math.min(maxH, h)) };
    }),
  drawerResizing: false,
  setDrawerResizing: (b) => set({ drawerResizing: b }),

  // ----- Feedback modal -----
  feedback: { open: false, target: null },
  openFeedback: (target = null) =>
    set({ feedback: { open: true, target } }),
  closeFeedback: () => set({ feedback: { open: false, target: null } }),

  // ----- Onboarding -----
  tourRunning: false,
  setTourRunning: (b) => set({ tourRunning: b }),
  coachNodeActive: false,
  setCoachNodeActive: (b) => set({ coachNodeActive: b }),
  keyPanelOpen: false,
  setKeyPanelOpen: (b) => set({ keyPanelOpen: b }),
  coachHighlight: null,
  setCoachHighlight: (a) => set({ coachHighlight: a }),

  // ----- Path tutorial (guided line playback) -----
  tutorial: null,
  startTutorial: (gameplanId, lineId) => {
    const s = get();
    const gp = s.gameplans.find((g) => g.id === gameplanId);
    const line = gp?.lines.find((l) => l.id === lineId);
    if (!gp || !line) return;
    const ordered = orderLineNodes(line);
    // Filter the map to this line and CLOSE the editor so the Drawer
    // surfaces each node's detail as playback walks it. gameplanFilter
    // + activeGameplanId + activeLineId make gameplanRefSets (App.tsx)
    // scope the visible graph to exactly this line.
    set({
      activeGameplanId: gameplanId,
      activeLineId: lineId,
      gameplanFilter: true,
      gameplanViewOpen: false,
      listViewOpen: false,
      tutorial: { lineId, step: 0 },
    });
    if (ordered.length > 0) get().setSelection(ordered[0].ref);
  },
  tutorialNext: () => {
    const s = get();
    if (!s.tutorial) return;
    const line = s.gameplans
      .flatMap((g) => g.lines)
      .find((l) => l.id === s.tutorial!.lineId);
    if (!line) return;
    const ordered = orderLineNodes(line);
    const last = Math.max(0, ordered.length - 1);
    const step = Math.min(s.tutorial.step + 1, last);
    set({ tutorial: { ...s.tutorial, step } });
    if (ordered[step]) get().setSelection(ordered[step].ref);
  },
  tutorialPrev: () => {
    const s = get();
    if (!s.tutorial) return;
    const line = s.gameplans
      .flatMap((g) => g.lines)
      .find((l) => l.id === s.tutorial!.lineId);
    if (!line) return;
    const ordered = orderLineNodes(line);
    const step = Math.max(0, s.tutorial.step - 1);
    set({ tutorial: { ...s.tutorial, step } });
    if (ordered[step]) get().setSelection(ordered[step].ref);
  },
  endTutorial: () => {
    // Clear playback, drop the selection, and reopen the plan editor —
    // "restore the normal plan view". The active gameplan / filter are
    // left as-is so the user lands back where they launched from.
    set({ tutorial: null });
    get().setSelection({ kind: 'none' });
    get().setGameplanViewOpen(true);
  },

  // ----- Gameplan editor -----
  gameplans: loadGameplans(),
  activeGameplanId: null,
  activeLineId: null,
  gameplanFilter: false,
  setGameplanFilter: (b) => set({ gameplanFilter: b }),

  createGameplan: (name, description) => {
    const gp = newGameplan(name, description);
    set((s) => ({ gameplans: [...s.gameplans, gp] }));
    return gp.id;
  },
  deleteGameplan: (id) =>
    set((s) => ({
      gameplans: s.gameplans.filter((g) => g.id !== id),
      // Clear active pointers if the deleted gameplan was being viewed.
      activeGameplanId: s.activeGameplanId === id ? null : s.activeGameplanId,
      activeLineId: s.activeGameplanId === id ? null : s.activeLineId,
    })),
  renameGameplan: (id, name) =>
    set((s) => ({
      gameplans: withGameplanUpdate(s.gameplans, id, (g) => ({ ...g, name })),
    })),
  setGameplanDescription: (id, description) =>
    set((s) => ({
      gameplans: withGameplanUpdate(s.gameplans, id, (g) => ({
        ...g,
        description,
      })),
    })),
  setActiveGameplan: (id) =>
    set((s) => {
      // When switching gameplans, default the active line to the
      // first line in the new plan so the editor lands on something.
      // If the same plan is re-activated, keep the existing line.
      if (id === null) {
        // Clearing the active plan also drops the gameplan filter so
        // the canvas reverts to the full graph, and ends any line
        // playback (the tutorial belongs to this plan). Selection /
        // drawer / history are left alone — the user might have a node
        // they selected while the plan was active that they want to keep
        // looking at.
        return {
          activeGameplanId: null,
          activeLineId: null,
          gameplanFilter: false,
          tutorial: null,
        };
      }
      const gp = s.gameplans.find((g) => g.id === id);
      const firstLine = gp?.lines[0]?.id ?? null;
      const sameAsBefore = s.activeGameplanId === id;
      return {
        activeGameplanId: id,
        activeLineId: sameAsBefore ? s.activeLineId : firstLine,
        // Default the graph to "this plan only" the moment a plan opens so
        // the canvas immediately reflects the selected gameplan.
        gameplanFilter: true,
        // Engaging a specific gameplan turns ON the canvas filter,
        // which may drop the currently-selected entity from the
        // visible set and leave Detail rendering a node that's no
        // longer in the filtered graph (bug #14 from the mobile UX
        // batch — Detail throws errors when the selection isn't in
        // the filter). Drop the selection on plan ACTIVATION (not
        // on opening the Plan tab) so:
        //   - Plan index / list view leaves selection untouched.
        //   - Picking a specific plan starts the user fresh inside
        //     that plan's filtered view.
        // We only deselect when switching INTO a different plan;
        // re-activating the same plan keeps the current selection
        // (which is presumably already inside its filter).
        ...(sameAsBefore
          ? {}
          : {
              selection: { kind: 'none' as const },
              drawerOpen: false,
              history: [],
              historyIndex: -1,
            }),
      };
    }),

  createLine: (gameplanId, name) => {
    const line = newLine(name);
    set((s) => ({
      gameplans: withGameplanUpdate(s.gameplans, gameplanId, (g) => ({
        ...g,
        lines: [...g.lines, line],
      })),
      // Switch focus to the new line — that's almost always what the
      // user wants right after creating one.
      activeLineId: line.id,
    }));
    return line.id;
  },
  deleteLine: (gameplanId, lineId) =>
    set((s) => ({
      gameplans: withGameplanUpdate(s.gameplans, gameplanId, (g) => ({
        ...g,
        lines: g.lines.filter((l) => l.id !== lineId),
      })),
      activeLineId: s.activeLineId === lineId ? null : s.activeLineId,
    })),
  renameLine: (gameplanId, lineId, name) =>
    set((s) => ({
      gameplans: withLineUpdate(s.gameplans, gameplanId, lineId, (l) => ({
        ...l,
        name,
      })),
    })),
  setLineNotes: (gameplanId, lineId, notes) =>
    set((s) => ({
      gameplans: withLineUpdate(s.gameplans, gameplanId, lineId, (l) => ({
        ...l,
        notes,
      })),
    })),
  setActiveLine: (lineId) => set({ activeLineId: lineId }),

  addLineNode: (gameplanId, lineId, ref) => {
    const node: LineNode = newLineNode(ref);
    set((s) => ({
      gameplans: withLineUpdate(s.gameplans, gameplanId, lineId, (l) => ({
        ...l,
        nodes: [...l.nodes, node],
      })),
    }));
    return node.id;
  },
  removeLineNode: (gameplanId, lineId, nodeId) =>
    set((s) => ({
      gameplans: withLineUpdate(s.gameplans, gameplanId, lineId, (l) => ({
        ...l,
        nodes: l.nodes.filter((n) => n.id !== nodeId),
        // Drop incident edges so the line graph stays consistent.
        edges: l.edges.filter(
          (e) => e.from !== nodeId && e.to !== nodeId,
        ),
      })),
    })),
  setLineNodeNotes: (gameplanId, lineId, nodeId, notes) =>
    set((s) => ({
      gameplans: withLineUpdate(s.gameplans, gameplanId, lineId, (l) => ({
        ...l,
        nodes: l.nodes.map((n) =>
          n.id === nodeId ? { ...n, notes } : n,
        ),
      })),
    })),

  addLineEdge: (gameplanId, lineId, fromId, toId, label) => {
    const edge: LineEdge = newLineEdge(fromId, toId, label);
    set((s) => ({
      gameplans: withLineUpdate(s.gameplans, gameplanId, lineId, (l) => ({
        ...l,
        edges: [...l.edges, edge],
      })),
    }));
    return edge.id;
  },
  removeLineEdge: (gameplanId, lineId, edgeId) =>
    set((s) => ({
      gameplans: withLineUpdate(s.gameplans, gameplanId, lineId, (l) => ({
        ...l,
        edges: l.edges.filter((e) => e.id !== edgeId),
      })),
    })),
  setLineEdgeLabel: (gameplanId, lineId, edgeId, label) =>
    set((s) => ({
      gameplans: withLineUpdate(s.gameplans, gameplanId, lineId, (l) => ({
        ...l,
        edges: l.edges.map((e) =>
          e.id === edgeId ? { ...e, label } : e,
        ),
      })),
    })),
}));

// ----- Gameplan persistence subscription -----
//
// Persist every change to the gameplans collection to localStorage.
// `subscribe` fires for every state update; we keep a module-local
// prev reference so we only write when the gameplans array actually
// changed identity (every mutation goes through immutable update
// helpers, so reference equality is a reliable diff). Active-id
// pointers stay session-only — no point persisting which line was
// last focused.
let _prevGameplans = useAppStore.getState().gameplans;
useAppStore.subscribe((state) => {
  if (state.gameplans !== _prevGameplans) {
    _prevGameplans = state.gameplans;
    saveGameplans(state.gameplans);
  }
});
